import type { ReactNode } from 'react';
import { useAuth } from './AuthProvider';

export interface Requirement {
  /** Todos estos permisos (ya expandidos por /v1/auth/me). */
  perm?: string | string[];
  /** Al menos uno de estos permisos. */
  anyPerm?: string[];
  /** Funcionalidad(es) del plan requeridas (todas). */
  feature?: string | string[];
  /** Al menos una de estas funcionalidades del plan. */
  anyFeature?: string[];
  platformAdmin?: boolean;
}

export function satisfies(
  me:
    | {
        permissions: string[];
        features: Record<string, boolean>;
        user: { isPlatformAdmin: boolean };
      }
    | undefined,
  requirement: Requirement,
): boolean {
  if (!me) return false;
  const perms =
    requirement.perm === undefined
      ? []
      : Array.isArray(requirement.perm)
        ? requirement.perm
        : [requirement.perm];
  if (!perms.every((p) => me.permissions.includes(p))) return false;
  if (requirement.anyPerm && !requirement.anyPerm.some((p) => me.permissions.includes(p)))
    return false;
  const features =
    requirement.feature === undefined
      ? []
      : Array.isArray(requirement.feature)
        ? requirement.feature
        : [requirement.feature];
  if (!features.every((f) => me.features[f] === true)) return false;
  if (requirement.anyFeature && !requirement.anyFeature.some((f) => me.features[f] === true))
    return false;
  if (requirement.platformAdmin && !me.user.isPlatformAdmin) return false;
  return true;
}

export function useCan(): (requirement: Requirement) => boolean {
  const { me } = useAuth();
  return (requirement) => satisfies(me, requirement);
}

/** Muestra el contenido solo si el usuario tiene el permiso y el plan incluye la funcionalidad. */
export function Can({
  children,
  fallback = null,
  ...requirement
}: Requirement & { children: ReactNode; fallback?: ReactNode }) {
  const can = useCan();
  return <>{can(requirement) ? children : fallback}</>;
}
